/**
 * Saved Service Business Logic Hooks
 *
 * TanStack Query hooks for saved service management
 * Optimized for caching with appropriate refresh intervals
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    checkSavedServiceExistanceApi,
    createSavedServiceApi,
    deleteSavedServiceApi,
    getSavedServiceApi,
    getSavedServiceDetailApi,
    updateSavedServiceApi,
} from './savedService.api';

// Query Keys for business saved service domain
export const businessSavedServiceKeys = {
    all: ['savedService'],
    savedServiceList: (params) => [...businessSavedServiceKeys.all, 'list', params],
    savedServiceInfiniteList: (params) => [...businessSavedServiceKeys.all, 'infiniteList', params],
    savedServiceDetail: (id) => [...businessSavedServiceKeys.all, 'detail', id],
};

/**
 * Hook to get saved services list with search and pagination
 *
 * Uses query for optimized loading and caching:
 * - Smart search debouncing
 * - Optimized caching with search parameters
 * - Background refetch for better UX
 * - Automatic cache invalidation on saved service changes
 *
 * @param {object} params - Query parameters
 * @param {string} params.search - Search term
 * @param {array} params.serviceCategory - Array of service category filters
 * @param {array} params.vehicleMake - Array of vehicle make filters
 * @param {boolean} params.pagination - Enable pagination (default: true)
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.limit - Number of items per page
 * @param {string} params.sortBy - Sort field
 * @param {string} params.sortOrder - Sort order (ASC/DESC)
 * @param {object} options - Query options
 * @returns {object} Query result with saved services data
 */
export const useGetSavedServiceList = (params = {}, options = {}) => {
    // Set default pagination if not provided
    const queryParams = {
        pagination: true,
        page: 1,
        limit: 25,
        sortBy: 'createdAt',
        sortOrder: 'DESC',
        ...params,
    };

    return useQuery({
        queryKey: businessSavedServiceKeys.savedServiceList(queryParams),
        queryFn: () => getSavedServiceApi(queryParams),
        staleTime: 30 * 1000, // 30 seconds - very short since saved services change frequently
        cacheTime: 2 * 60 * 1000, // 2 minutes - shorter cache time for faster updates
        ...options,
    });
};

/**
 * Hook to get infinite saved services list with search and pagination
 *
 * Uses infinite query for optimized loading and caching:
 * - Infinite scroll pagination
 * - Smart search debouncing
 * - Optimized caching with search parameters
 * - Background refetch for better UX
 * - Automatic cache invalidation on saved service changes
 *
 * @param {object} params - Query parameters
 * @param {string} params.search - Search term
 * @param {array} params.serviceCategory - Array of service category filters
 * @param {array} params.vehicleMake - Array of vehicle make filters
 * @param {number} params.limit - Number of items per page (default: 50)
 * @param {string} params.sortBy - Sort field
 * @param {string} params.sortOrder - Sort order (ASC/DESC)
 * @param {object} options - Query options
 * @returns {object} Infinite query result with saved services data
 */
export const useInfiniteSavedServiceList = (params = {}, options = {}) => {
    // Set default parameters for infinite scroll
    const queryParams = {
        pagination: true,
        limit: 50,
        ...params,
    };

    return useInfiniteQuery({
        queryKey: businessSavedServiceKeys.savedServiceInfiniteList(queryParams),
        queryFn: ({ pageParam = 1 }) => {
            return getSavedServiceApi({
                ...queryParams,
                page: pageParam,
            });
        },
        getNextPageParam: (lastPage, allPages) => {
            // Check if there are more pages
            if (lastPage?.totalPages && allPages.length < lastPage.totalPages) {
                return allPages.length + 1;
            }
            return undefined;
        },
        getPreviousPageParam: (firstPage, allPages) => {
            // For previous page support (optional)
            if (allPages.length > 1) {
                return allPages.length - 1;
            }
            return undefined;
        },
        refetchOnWindowFocus: false, // Prevent refetch on window focus for better performance
        keepPreviousData: true, // Keep previous data while fetching new data
        ...options,
    });
};
/**
 * Hook to get saved service detail by ID
 *
 * Uses optimized caching for saved service detail:
 * - 5-minute cache (moderately changes)
 * - Auto refresh on window focus
 * - Background refetch for better UX
 *
 * @param {string} id - Saved service ID
 * @param {object} options - Query options
 * @returns {object} Query result with saved service detail data
 */
export const useGetSavedServiceDetail = (id, options = {}) => {
    return useQuery({
        queryKey: businessSavedServiceKeys.savedServiceDetail(id),
        queryFn: () => getSavedServiceDetailApi(id),
        enabled: !!id, // Only run query if id is provided
        ...options,
    });
};

/**
 * Hook to create new saved service
 *
 * Automatically invalidates and refetches saved service lists after creation
 * Shows success/error notifications based on mutation result
 *
 * @param {object} options - Mutation options
 * @returns {object} Mutation result with create function
 */
export const useCreateSavedService = (options = {}) => {
    return useMutation({
        mutationFn: createSavedServiceApi,
        ...options,
    });
};

/**
 * Hook to update existing saved service
 *
 * Automatically invalidates and refetches saved service lists after update
 * Shows success/error notifications based on mutation result
 *
 * @param {object} options - Mutation options
 * @returns {object} Mutation result with update function
 */
export const useUpdateSavedService = (options = {}) => {
    return useMutation({
        mutationFn: ({ id, payload }) => updateSavedServiceApi(id, payload),
        ...options,
    });
};

/**
 * Hook to delete saved service
 *
 * Automatically invalidates and refetches saved service lists after deletion
 * Shows success/error notifications based on mutation result
 *
 * @param {object} options - Mutation options
 * @returns {object} Mutation result with delete function
 */
export const useDeleteSavedService = (options = {}) => {
    return useMutation({
        mutationFn: deleteSavedServiceApi,
        ...options,
    });
};

/**
 * Hook to check if saved service already exists
 *
 * This hook provides a mutation for checking service existence
 * Useful for preventing duplicate saved services
 *
 * @param {object} options - Mutation options
 * @returns {object} Mutation result with check function
 */
export const useCheckSavedServiceExistence = (options = {}) => {
    return useMutation({
        mutationFn: checkSavedServiceExistanceApi,
        ...options,
    });
};

/**
 * Hook to refetch saved service list (for use after saved service creation/updates)
 *
 * This hook provides a function to manually refetch saved service data
 * Useful after creating new saved services or making changes that require fresh data
 *
 * @param {object} params - Current query parameters to refetch
 * @returns {function} Refetch function
 */
export const useRefetchSavedServiceList = (params = {}) => {
    const queryClient = useQueryClient();

    return () => {
        queryClient.refetchQueries({ queryKey: businessSavedServiceKeys.all, exact: false });
    };
};
